import { useContext } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { UserContext } from '~/components/providers/UserProvider';

import {
  getBulkPicksForEntries,
  getUserEntriesForContest,
} from '../domains/services/entries.service.api';
import { ContestQueryKeys } from '../constants/query';
import type { FullContest } from '../domains/services/interfaces/full-contest.interface';

type UseContestEntriesWithPicksProps = {
  contest: FullContest;
  contestId: string;
  pageSize?: number;
  enabled?: boolean;
};

const usePlayerContestEntriesWithPicks = ({
  contest,
  contestId,
  pageSize = 20,
  enabled,
}: UseContestEntriesWithPicksProps) => {
  const { user } = useContext(UserContext);
  const userId = user?.id;

  // eslint-disable-next-line local-rules/require-query-stale-time
  const data = useInfiniteQuery({
    queryKey: ContestQueryKeys.ENTRIES_CURRENT_USER_WITH_PICKS({ contestId, userId }),
    queryFn: async ({ pageParam }) => {
      const entriesResponse = await getUserEntriesForContest(
        contestId,
        userId,
        pageSize,
        pageParam ?? 0
      );

      const picks = await getBulkPicksForEntries({
        entryIds: entriesResponse.data.map(({ id }) => id),
        slateIds: contest.slates.map(({ id }) => id),
      });

      const entriesData = entriesResponse.data;
      if (!entriesData || !entriesData.length) {
        return {
          entries: [],
          teamPicks: {},
          offset: entriesResponse.offset,
          total: entriesResponse.total,
        };
      }
      return {
        entries: entriesData,
        picks,
        total: entriesResponse.total,
        offset: entriesResponse.offset,
      };
    },
    getNextPageParam: (lastPage) =>
      lastPage.entries.length === pageSize ? lastPage.offset + pageSize : undefined,
    enabled: enabled && !!contestId && !!userId,
  });

  return {
    ...data,
    itemsTotal: data.data?.pages[0]?.total,
  };
};

export default usePlayerContestEntriesWithPicks;
